import { useEffect, useState } from 'react';
import { Building2, FileText, KanbanSquare, Megaphone, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface SupplierRow {
  id: string;
  name: string;
  company?: string | null;
  contact_email?: string | null;
}

interface SummaryData {
  suppliers: SupplierRow[];
  counts: {
    activities_open: number;
    campaigns_total: number;
    content_in_review: number;
    documents: number;
  };
}

interface SupplierPortalPageProps {
  onNavigate: (path: string) => void;
}

export function SupplierPortalPage({ onNavigate }: SupplierPortalPageProps) {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.get<{ success?: boolean; data?: SummaryData; error?: string }>('/supplier-portal/summary');
      if (res.success && res.data) setSummary(res.data);
      else setError(res.error || 'Daten konnten nicht geladen werden.');
    } catch {
      setError('Daten konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const counts = summary?.counts;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Zulieferer-Portal</h1>
          <p className="mt-1 max-w-2xl text-muted">
            Ihre freigegebenen Aktivitäten, Kampagnen und Dokumente — gebunden an die Zulieferer-Zugriffe Ihres Kontos.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()} disabled={loading} className="shrink-0 gap-2">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
          {error}
        </div>
      )}

      {summary && summary.suppliers.length > 0 && (
        <Card className="w-full border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-foreground dark:text-muted" />
              Ihre Zulieferer
            </CardTitle>
            <CardDescription>Für diese Profile haben Sie Zugriff.</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {summary.suppliers.map((s) => (
                <li key={s.id} className="flex flex-col gap-0.5 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium text-foreground">{s.name}</p>
                    {s.company && <p className="text-sm text-muted">{s.company}</p>}
                  </div>
                  {s.contact_email && (
                    <a
                      href={`mailto:${s.contact_email}`}
                      className="text-sm text-foreground hover:underline dark:text-muted"
                    >
                      {s.contact_email}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {summary && summary.suppliers.length === 0 && !loading && (
        <Card className="w-full border-warning/30 bg-warning-soft">
          <CardContent className="py-8 text-center text-muted">
            <p className="font-medium">Noch kein Zulieferer-Zugriff</p>
            <p className="mt-2 text-sm text-muted">
              Bitte wenden Sie sich an Leftclick AG, damit Ihr Konto einem Zulieferer zugeordnet wird.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid w-full gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <button
          type="button"
          onClick={() => onNavigate('/activities')}
          className="group flex w-full flex-col rounded-xl border border-border bg-card p-5 text-left shadow-sm transition hover:border-border hover:shadow-md"
        >
          <KanbanSquare className="h-8 w-8 text-foreground dark:text-muted" />
          <span className="mt-3 text-lg font-semibold text-foreground">Aktivitäten</span>
          <span className="mt-1 text-3xl font-bold tabular-nums text-foreground">
            {loading ? '—' : counts?.activities_open ?? 0}
          </span>
          <span className="mt-2 text-sm text-muted">Offen (nicht erledigt)</span>
        </button>

        <button
          type="button"
          onClick={() => onNavigate('/campaigns')}
          className="group flex w-full flex-col rounded-xl border border-border bg-card p-5 text-left shadow-sm transition hover:border-border hover:shadow-md"
        >
          <Megaphone className="h-8 w-8 text-foreground dark:text-muted" />
          <span className="mt-3 text-lg font-semibold text-foreground">Kampagnen</span>
          <span className="mt-1 text-3xl font-bold tabular-nums text-foreground">
            {loading ? '—' : counts?.campaigns_total ?? 0}
          </span>
          <span className="mt-2 text-sm text-muted">Aktive Kampagnen</span>
        </button>

        <button
          type="button"
          onClick={() => onNavigate('/campaigns')}
          className="group flex w-full flex-col rounded-xl border border-border bg-card p-5 text-left shadow-sm transition hover:border-border hover:shadow-md"
        >
          <Megaphone className="h-8 w-8 text-warning" />
          <span className="mt-3 text-lg font-semibold text-foreground">Content in Review</span>
          <span className="mt-1 text-3xl font-bold tabular-nums text-foreground">
            {loading ? '—' : counts?.content_in_review ?? 0}
          </span>
          <span className="mt-2 text-sm text-muted">In Freigabe / Review</span>
        </button>

        <button
          type="button"
          onClick={() => onNavigate('/documents')}
          className="group flex w-full flex-col rounded-xl border border-border bg-card p-5 text-left shadow-sm transition hover:border-border hover:shadow-md"
        >
          <FileText className="h-8 w-8 text-foreground dark:text-muted" />
          <span className="mt-3 text-lg font-semibold text-foreground">Dokumente</span>
          <span className="mt-1 text-3xl font-bold tabular-nums text-foreground">
            {loading ? '—' : counts?.documents ?? 0}
          </span>
          <span className="mt-2 text-sm text-muted">Sichtbare Dateien</span>
        </button>
      </div>
    </div>
  );
}
